import React, { useRef, useState } from 'react';
import useShowCurrency from 'framework/base/hooks/useShowCurrency';
import useFrameworkTranslation from 'framework/language/useFrameworkTranslation';
import AryPopoverTableCalculation from './AryPopoverTableCalculation';
import AryPinnedTable from './AryPinnedTable';

const ArySelectablePinnedTable = ({
  data,
  columns,
  pinnedColumnsList,
  noContentBox,
  customNoContent,
  handleChangeStatistics,
  tableSx,
  stickyHeadSx,
  onHover,
  isFlat,
  page,
  pageCount,
  tableRef,
  isTableSelectable
}) => {
  const pinnedTableRef = useRef(null);
  const popOverRef = useRef(null);
  const showCurrency = useShowCurrency();
  const { t } = useFrameworkTranslation();

  /** ******************************* states ************************************* */
  const [clickedRowIndex, setClickedRowIndex] = useState();
  const [popoverPosition, setPopoverPosition] = useState({});
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [invalidCalculationColumns, setInvalidCalculationColumns] = useState(
    []
  );

  /** ******************************* states ************************************* */

  /** ******************************* functions ************************************* */

  const handlePopoverOpen = (
    rowIndex,
    columnIndex,
    Rows,
    Cols,
    invalidCols
  ) => {
    // Calculate popover position based on the last selected cell
    if (rowIndex && columnIndex && invalidCols.length !== Cols.length) {
      const cellRect = pinnedTableRef.current
        .querySelector(`#cell-${rowIndex}-${columnIndex}`)
        .getBoundingClientRect();
      const popoverX = cellRect.left + cellRect.width / 2;
      const popoverY = cellRect.bottom;
      setPopoverPosition({ top: popoverY, left: popoverX });
      setSelectedRows(Rows);
      setSelectedColumns(Cols);
      setInvalidCalculationColumns(invalidCols);
    } else {
      setPopoverPosition({});
      setSelectedRows([]);
      setSelectedColumns([]);
      setInvalidCalculationColumns([]);
    }
  };

  const toCamelCase = (str) =>
    str
      .replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) =>
        index === 0 ? word.toLowerCase() : word.toUpperCase()
      )
      .replace(/\s+/g, '');

  const handleCalculation = (calculationKey) => {
    if (calculationKey !== '') {
      const objField = toCamelCase(
        pinnedColumnsList[pinnedColumnsList.indexOf(calculationKey)]
      );

      const statistics = {};

      selectedRows.forEach((row) => {
        const code =
          data[row].currencyCode !== undefined
            ? data[row].currencyCode
            : data[row].draftCurrencyCode !== undefined
            ? data[row].draftCurrencyCode
            : data[row].currencyTitle !== undefined
            ? showCurrency({ title: data[row].currencyTitle })
            : data[row].draftCurrencyTitle !== undefined
            ? showCurrency({ title: data[row].draftCurrencyTitle })
            : t('currency');
        const value = Number(data[row][objField]);

        if (!statistics[code]) {
          statistics[code] = {
            column: {
              value: columns[calculationKey].label,
              label: t('table.column')
            },
            count: { value: 0, label: t('table.rowCount') },
            min: { value, label: t('table.minimum') },
            max: { value, label: t('table.maximum') },
            sum: { value: 0, label: t('table.sum') },
            avg: { value: 0, label: t('table.average') }
          };
        }

        statistics[code].sum = {
          ...statistics[code].sum,
          value: statistics[code].sum.value + value
        };
        statistics[code].min = {
          ...statistics[code].min,
          value: Math.min(statistics[code].min.value, value)
        };
        statistics[code].max = {
          ...statistics[code].max,
          value: Math.max(statistics[code].max.value, value)
        };
        statistics[code].count.value++;
      });

      Object.keys(statistics).forEach((field) => {
        statistics[field].avg = {
          ...statistics[field].avg,
          value: statistics[field].sum.value / statistics[field].count.value
        };
      });

      statistics.show = true;

      handleChangeStatistics(statistics);
    }
  };

  return (
    <>
      <AryPinnedTable
        ref={pinnedTableRef}
        popOverRef={popOverRef}
        handleChangeStatistics={handleChangeStatistics}
        pinnedColumnsList={pinnedColumnsList}
        noContentBox={noContentBox}
        customNoContent={customNoContent}
        data={data}
        columns={columns}
        tableSx={tableSx}
        stickyHeadSx={stickyHeadSx}
        onHover={onHover}
        isFlat={isFlat}
        page={page}
        pageCount={pageCount}
        clickedRowIndex={clickedRowIndex}
        setClickedRowIndex={setClickedRowIndex}
        tableRef={tableRef}
        handlePopoverOpen={handlePopoverOpen}
        isTableSelectable={isTableSelectable}
        handleResetStatistics={() => handleChangeStatistics({})}
      />
      <AryPopoverTableCalculation
        ref={popOverRef}
        open={!!popoverPosition.top}
        anchorPosition={popoverPosition}
        handleClose={() => setPopoverPosition({})}
        handleChangeCalculationKey={(newValue) => handleCalculation(newValue)}
        selectedColumns={selectedColumns}
        invalidCalculationColumns={invalidCalculationColumns}
        filteredColumns={pinnedColumnsList}
        columns={columns}
      />
    </>
  );
};

export default ArySelectablePinnedTable;
